import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import debounce from 'lodash/debounce'

import { MenuSearchMode } from 'src/apollo/sites'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { getSearchRegExp } from './searchFilterUtils'

export type SearchContextType = {
  searchString: string
  setSearchString: (value: string) => void
  clearSearchString: () => void
  canUseSearch: boolean
  searchRegExp: RegExp
  isSearching: boolean
  setIsSearching: (isSearching: boolean) => void
  recentSearches: string[]
  clearRecentSearches: () => void
  fromRecentSearches: boolean
  setFromRecentSearches: (fromRecent: boolean) => void
}

export const defaultSearchContext = {
  searchString: '',
  setSearchString: () => {},
  clearSearchString: () => {},
  canUseSearch: false,
  searchRegExp: /^\b$/, // this regex for default case never matches anything
  isSearching: false,
  setIsSearching: () => {},
  recentSearches: [],
  clearRecentSearches: () => {},
  fromRecentSearches: false,
  setFromRecentSearches: () => {}
}

const RECENT_SEARCHES_KEY = 'recent-searches'

export const SearchContext =
  React.createContext<SearchContextType>(defaultSearchContext)

export const MenuSearchContextProvider = ({
  children
}: React.PropsWithChildren<{}>) => {
  const { restaurant } = useRestaurant()

  const canUseSearch =
    restaurant.config.ooConfig?.format?.menuSearchMode !== MenuSearchMode.Hidden

  const [searchString, setSearchString] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [fromRecentSearches, setFromRecentSearches] = useState(false)

  const debouncedSetSearchString = useMemo(
    () => debounce((value: string) => setSearchString(value), 500),
    [setSearchString]
  )

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined'
    ) {
      setRecentSearches(
        localStorage.getItem(`${RECENT_SEARCHES_KEY}|${restaurant.externalId}`)
          ? JSON.parse(
              localStorage.getItem(
                `${RECENT_SEARCHES_KEY}|${restaurant.externalId}`
              ) as string
            )
          : []
      )
    }
  }, [restaurant.externalId])

  useMemo(() => {
    if (searchString && searchString.length >= 2) {
      setRecentSearches((prevSearches) => {
        const updatedSearches = [
          searchString,
          ...prevSearches.filter((s) => s !== searchString)
        ]

        // Only keep the most 8 recent searches
        if (
          typeof window !== 'undefined' &&
          typeof window.localStorage !== 'undefined'
        ) {
          localStorage.setItem(
            `${RECENT_SEARCHES_KEY}|${restaurant.externalId}`,
            JSON.stringify(updatedSearches.slice(0, 8))
          )
        }

        return updatedSearches.slice(0, 8)
      })
    }
  }, [searchString, setRecentSearches, restaurant.externalId])

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([])
    window.localStorage.removeItem(
      `${RECENT_SEARCHES_KEY}|${restaurant.externalId}`
    )
  }, [setRecentSearches, restaurant.externalId])

  // sets the search string to '' without debounce
  const clearSearchString = useCallback(() => {
    setSearchString('')
    setFromRecentSearches(false)
    // also clear the search string with the debounce method to override any currently waiting debounce calls
    debouncedSetSearchString('')
  }, [setSearchString, setFromRecentSearches, debouncedSetSearchString])

  const contextData = useMemo(
    () => ({
      searchString,
      setSearchString: debouncedSetSearchString,
      clearSearchString,
      canUseSearch,
      searchRegExp: getSearchRegExp(searchString),
      isSearching,
      setIsSearching,
      recentSearches,
      clearRecentSearches,
      fromRecentSearches,
      setFromRecentSearches
    }),
    [
      searchString,
      debouncedSetSearchString,
      clearSearchString,
      canUseSearch,
      isSearching,
      setIsSearching,
      recentSearches,
      clearRecentSearches,
      fromRecentSearches,
      setFromRecentSearches
    ]
  )

  return (
    <SearchContext.Provider value={contextData}>
      {children}
    </SearchContext.Provider>
  )
}

export const useMenuSearchContext = () => {
  return useContext(SearchContext)
}
